import React, {Suspense} from 'react';
import PropTypes from 'prop-types';
import {createPortal} from 'react-dom';
import EbookDetailsModal from '@/features/search/components/EbookDetailsModal';
import {useGetLocaleQuery} from '@/services/api/locale-api';
import {useSelectedZone} from '@/services/geoblocking/hooks/useSelectedZone';
import {useAnalytics} from '@/services/analytics/hooks/useAnalytics';
import {useEcommercePageView} from '@/services/analytics/hooks/useEcommercePageView';
import {useDetailsModal} from '@/features/search/hooks/useDetailsModal';
import {
  EVENTS,
  ANALYTICS_SOURCE_TYPES,
  ANALYTICS_PAGE_TYPES,
  ANALYTICS_SUBPAGE_TYPES,
} from '@/services/analytics/constants';

const LazyCartFlyout = React.lazy(() =>
  import(/* webpackChunkName: "cartFlyout" */ '@/features/cart/components/CartFlyoutContainer').then(
    (module) => ({
      default: module.default,
    }),
  ),
);

const EbookResults = ({children}) => {
  const zone = useSelectedZone();
  const {data: locale} = useGetLocaleQuery({zone}, {skip: !zone});
  const {activeCard, openDetails, closeDetails, isOpen} = useDetailsModal();
  const {trackEvent} = useAnalytics();
  useEcommercePageView({pageType: ANALYTICS_PAGE_TYPES.SEARCH, pageSubType: ANALYTICS_SUBPAGE_TYPES.EBOOKS, zone});

  const handleViewDetailsClick = (cardinfo, labels) => {
    trackEvent(EVENTS.PRODUCT_VIEW, cardinfo, {sourceType: ANALYTICS_SOURCE_TYPES.EBOOK_SEARCH_RESULT_ITEM, currency: locale?.currency});
    openDetails(cardinfo, labels);
  };

  const cards = React.Children.map(children, (child) =>
    React.cloneElement(child, {
      onViewDetailsClick: () => handleViewDetailsClick(child.props.cardinfo, child.props.labels),
    }),
  );

  let detailsModalPortal = null;
  if (typeof window !== 'undefined' && document.body) {
    detailsModalPortal = createPortal(
      <EbookDetailsModal
        open={isOpen}
        cardinfo={activeCard?.cardinfo}
        labels={activeCard?.labels}
        onClose={closeDetails}
      />,
      document.body,
    );
  }

  const flyout = (
    <Suspense fallback={<></>}>
      <LazyCartFlyout hideMoneyBackMessage={true} />
    </Suspense>
  );

  return (
    <>
      {cards}
      {detailsModalPortal}
      {flyout}
    </>
  );
};

EbookResults.propTypes = {
  children: PropTypes.node,
};

export default EbookResults;
